import { AnomalyCameraType, AnomalyPerspectiveCamera } from '@api/twin/AnomalyApi';
import { useTeiaViewerContext, type SceneMesh, type Viewer } from '@stereograph/teiaviewer';
import { Box3, Sphere, PerspectiveCamera as ThreePerspective, Vector3 } from 'three';
import { DEFAULT_ANOMALY_PERSPECTIVE_CAMERA } from '../widgets/utils';

const _box = new Box3();
const _tmp = new Box3();
const _sphere = new Sphere();

const translateVectorToTeia = (v: Vector3): Vector3 => {
  return new Vector3(v.x, -v.z, v.y);
};

const sphereFromUuids = (viewer: Viewer, uuids: Iterable<string>): Sphere | null => {
  _box.makeEmpty();
  for (const obj of viewer.scene.getObjectsByUUID(uuids)) {
    const mesh = obj as SceneMesh;
    if (mesh?.isSceneMesh) {
      _box.union(mesh.getBoundingBox(_tmp));
    }
  }
  if (_box.isEmpty()) {
    return null;
  }
  _box.getBoundingSphere(_sphere);

  return _sphere.clone();
};

const getSphereForScene = (viewer: Viewer): Sphere | null => {
  const sceneSphere = new Sphere();
  viewer.scene.getBoundingSphere(sceneSphere);
  if (isFinite(sceneSphere.radius) && sceneSphere.radius > 0) {
    return sceneSphere;
  }

  const visible = Array.from(viewer.scene.getAllVisibleObjectUuids());
  const hidden = Array.from(viewer.scene.getAllHiddenObjectUuids());

  return sphereFromUuids(viewer, [...visible, ...hidden]);
};

export const useAnomalyPerspectiveCamera = (
  radiusFactor = 1.7,
): AnomalyPerspectiveCamera => {
  const { viewer } = useTeiaViewerContext();

  if (!viewer?.viewport) {
    return DEFAULT_ANOMALY_PERSPECTIVE_CAMERA;
  }

  const cameraControls = viewer.viewport.cameraControls;
  const camera = cameraControls.camera as ThreePerspective;

  if (!camera?.isPerspectiveCamera) {
    return DEFAULT_ANOMALY_PERSPECTIVE_CAMERA;
  }

  const position = new Vector3();
  const target = new Vector3();
  const direction = new Vector3();

  cameraControls.getPosition(position);
  cameraControls.getTarget(target);
  camera.getWorldDirection(direction);

  const selection = new Set<string>(viewer.scene.objectSelection.getUuids());
  const visible = Array.from(viewer.scene.getAllVisibleObjectUuids());
  const selVisible = visible.filter((id) => selection.has(id));
  let sphere: Sphere | null = selVisible.length ? sphereFromUuids(viewer, selVisible) : null;

  sphere ??= getSphereForScene(viewer);

  if (!sphere) {
    const forward = direction.lengthSq() > 0
      ? direction.clone()
      : target.clone().sub(position)
        .normalize();

    return {
      type: AnomalyCameraType.PERSPECTIVE,
      cameraViewPoint: translateVectorToTeia(position),
      cameraDirection: translateVectorToTeia(forward),
      cameraUpVector: new Vector3(0, 0, 1),
      aspectRatio: camera.aspect,
      fieldOfView: camera.fov,
    };
  }

  const center = sphere.center;
  const distance = sphere.radius * radiusFactor;
  const forward = direction.lengthSq() > 0
    ? direction.clone()
    : center.clone().sub(position)
      .normalize();

  const newPosition = center.clone().sub(forward.clone().multiplyScalar(distance));
  const newDirection = center.clone().sub(newPosition)
    .normalize();

  return {
    type: AnomalyCameraType.PERSPECTIVE,
    cameraViewPoint: translateVectorToTeia(newPosition),
    cameraDirection: translateVectorToTeia(newDirection),
    cameraUpVector: new Vector3(0, 0, 1),
    aspectRatio: camera.aspect,
    fieldOfView: camera.fov,
  };
};
